import React, { useCallback, useMemo, useRef, useState } from "react";
import ListHeader from "../../common/list/ListHeader";
import PeriodFilter from '../../common/filters/Period';
import StoreFilter from '../../common/filters/Store';
import SendingTypeFilter from "../../common/filters/SendingType";
import { Alert, Form } from "react-bootstrap";
import { useGetEmailsQuery } from "../../../services/api/repositories/EmailsRepository";
import moment from "moment";
import TableMails from "./TableMails";
import NavigationCursorPagination from "../../common/NavigationCursorPagination";

export function MailsSection() {

    const [showAlert, setShowAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");


    const [cursor, setCursor] = useState<string | null>(null);
    const [perPage, setPerPage] = useState(10);

    const getStartOfWeek = () => {
        return moment().startOf('isoWeek').startOf('day').toDate();
    };

    const formatDate = (date: Date | null): string => {
        return moment(date).format('YYYY-MM-DD HH:mm:ss.SSS');
    };


    const defaultDateRange = useMemo<[Date | null, Date | null]>(() => [getStartOfWeek(), new Date()], []);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(defaultDateRange);

    const [dateStart, setDateStart] = useState<string>(dateRange[0] ? formatDate(dateRange[0]) : '');
    const [dateEnd, setDateEnd] = useState<string>(dateRange[1] ? formatDate(dateRange[1]) : '');

    const [type, setType] = useState<string>('all');
    const [store, setStore] = useState<string>('all');
    const [noIkea, setNoIkea] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const typeRef = useRef<HTMLSelectElement>(null);
    const storeRef = useRef<HTMLSelectElement>(null);
    const noIkeaRef = useRef<HTMLInputElement>(null);
    const emailRef = useRef<HTMLInputElement>(null);

    const { data, isLoading, isFetching, refetch } = useGetEmailsQuery(
        {
            cursor: cursor,
            per_page: perPage,
            date_start: dateStart,
            date_end: dateEnd,
            type: type,
            store: store,
            no_ikea: noIkea,
            email: email,
        },
        { refetchOnMountOrArgChange: true }
    );

    const hasSearchParametersChanged = useCallback(() => {
        if (typeRef.current?.value !== type || storeRef.current?.value !== store || noIkeaRef.current?.value !== noIkea || emailRef.current?.value !== email) {
            return true;
        }

        if (dateRange[0] === null && dateStart !== '') {
            return true;
        }

        if (dateRange[1] === null && dateEnd !== '') {
            return true;
        }

        if (dateRange[0] !== null && dateStart !== `${dateRange[0].toLocaleDateString()} 00:00:00`) {
            return true;
        }

        if (dateRange[1] !== null && dateEnd !== `${dateRange[1].toLocaleDateString()} 23:59:59`) {
            return true;
        }

        return false;
    }, [dateRange, dateEnd, dateStart, email, noIkea, store, type]);

    const handleSubmit = useCallback(() => {

        if (!hasSearchParametersChanged()) {
            refetch();
            return;
        }

        if (dateRange[0] === null || dateRange[1] === null) {
            setErrorMessage("Veuillez sélectionner une période d'exploitation.");
            setShowAlert(true);
            return;
        }

        setShowAlert(false);
        setErrorMessage("");

        setNoIkea(noIkeaRef.current?.value || '');
        setEmail(emailRef.current?.value || '');
        setType(typeRef.current?.value || 'all');
        setStore(storeRef.current?.value || 'all');

        setDateStart(formatDate(dateRange[0]));
        const endDate = new Date(dateRange[1]);
        endDate.setHours(23, 59, 59);
        setDateEnd(formatDate(endDate));

    }, [refetch, dateRange, hasSearchParametersChanged]);

    const handleReset = useCallback(() => {
        typeRef.current!.value = 'all';
        storeRef.current!.value = 'all';
        noIkeaRef.current!.value = '';
        emailRef.current!.value = '';
        setDateRange(defaultDateRange);

        setNoIkea('');
        setEmail('');
        setType('all');
        setStore('all');
        setDateStart(formatDate(getStartOfWeek()));
        setDateEnd(formatDate(new Date()));
    }, [defaultDateRange]);

    const [resendEmailResponseMessage, setResendEmailResponseMessage] = useState<string | null>(null);

    return (
        <>
            <ListHeader
                handleSubmit={handleSubmit}
                handleReset={handleReset}
            >
                <div className="search">
                    <Form.Group className="form-group search-loyaltyID">
                        <Form.Label>
                            <span className="icon"></span>
                            <span className="text">N° IKEA Family/Business</span>
                        </Form.Label>
                        <Form.Control type="text" ref={noIkeaRef} />
                    </Form.Group>
                    <Form.Group className="form-group search-email">
                        <Form.Label>
                            <span className="icon"></span>
                            <span className="text">Email Destinataire</span>
                        </Form.Label>
                        <Form.Control type="text" ref={emailRef} />
                    </Form.Group>
                </div>
                <div className="filters">
                    <PeriodFilter tabIndex={1} dateRange={dateRange} setDateRange={setDateRange} />
                    <SendingTypeFilter tabIndex={2} ref={typeRef} />
                    <StoreFilter tabIndex={3} ref={storeRef} />
                </div>

                {showAlert && errorMessage && (
                    <Alert variant="danger" onClose={() => setShowAlert(false)} dismissible>
                        {errorMessage}
                    </Alert>
                )}

            </ListHeader>


            {(isLoading || isFetching) && <p>Chargement...</p>}

            {((!isLoading && !isFetching) && data !== undefined) && (
                <>
                    {showAlert && resendEmailResponseMessage && (
                        <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
                            {resendEmailResponseMessage}
                        </Alert>
                    )}

                    {("message" in data) && <p>{data.message}</p>}
                    {("emails" in data) &&
                        (<>
                            <TableMails
                                data={data}
                                setResendEmailResponseMessage={setResendEmailResponseMessage}
                                setShowAlert={setShowAlert}
                            />
                            <NavigationCursorPagination
                                nextCursor={data.next_cursor}
                                prevCursor={data.prev_cursor}
                                setCursor={setCursor}
                                perPage={perPage}
                                setPerPage={setPerPage}
                            />

                        </>)}
                </>
            )
            }

            {((!isLoading && !isFetching) && data === undefined) &&
                <p>Une erreur a été rencontrée.</p>
            }
        </>
    );
}